import React, { useState, useEffect } from 'react';
import { apiFetch } from '../services/api_service';
import { API } from '../App';
import { Link } from 'react-router-dom';
import { 
  TrendingUp, 
  TrendingDown,
  CheckCircle, 
  AlertTriangle, 
  FileText, 
  ArrowRight,
  Leaf,
  Users,
  Building2,
  Target,
  BarChart3,
  Shield,
  ChevronRight,
  Sparkles
} from 'lucide-react';
import { 
  PieChart, 
  Pie, 
  Cell, 
  ResponsiveContainer, 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip,
  RadialBarChart,
  RadialBar
} from 'recharts';

// Compliance Score Ring Component
const ComplianceScoreRing = ({ score, size = 180 }) => {
  const data = [{ name: 'Score', value: score, fill: '#064E3B' }];
  
  return (
    <div className="relative" style={{ width: size, height: size, minWidth: size, minHeight: size }}>
      <ResponsiveContainer width={size} height={size} minWidth={size} minHeight={size}>
        <RadialBarChart 
          cx="50%" 
          cy="50%" 
          innerRadius="70%" 
          outerRadius="100%" 
          data={data}
          startAngle={90}
          endAngle={-270}
        >
          <RadialBar
            background={{ fill: '#E2E8F0' }}
            dataKey="value"
            cornerRadius={10}
          />
        </RadialBarChart>
      </ResponsiveContainer>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-4xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {score}%
        </span>
        <span className="text-xs text-slate-500 uppercase tracking-wider">Compliance</span>
      </div>
    </div>
  );
};

// KPI Card Component
const KPICard = ({ label, value, delta, deltaType, icon: Icon, color = 'emerald' }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-6 flex flex-col" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-start justify-between mb-3">
      <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">{label}</span>
      {Icon && (
        <div className={`w-8 h-8 rounded-md flex items-center justify-center ${
          color === 'emerald' ? 'bg-emerald-50 text-emerald-700' :
          color === 'pink' ? 'bg-pink-50 text-pink-700' :
          color === 'blue' ? 'bg-blue-50 text-blue-700' :
          color === 'amber' ? 'bg-amber-50 text-amber-700' :
          'bg-slate-50 text-slate-700'
        }`}>
          <Icon size={16} />
        </div>
      )}
    </div>
    <div className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{value}</div>
    {delta !== undefined && (
      <div className={`text-sm font-medium mt-1 flex items-center gap-1 ${
        deltaType === 'positive' ? 'text-emerald-600' : 
        deltaType === 'negative' ? 'text-red-600' : 
        'text-slate-500'
      }`}>
        {deltaType === 'positive' ? <TrendingUp size={14} /> : deltaType === 'negative' ? <TrendingDown size={14} /> : null}
        <span>{delta}</span>
      </div>
    )}
  </div>
);

// Quick Action Card
const QuickActionCard = ({ title, description, icon: Icon, to, color = 'emerald' }) => (
  <Link
    to={to}
    data-testid={`quick-action-${title.toLowerCase().replace(/\s+/g, '-')}`}
    className="group flex items-center gap-4 p-4 bg-white border border-slate-200 rounded-lg hover:border-emerald-300 hover:shadow-sm transition-all"
  >
    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
      color === 'emerald' ? 'bg-emerald-50 text-emerald-700' :
      color === 'pink' ? 'bg-pink-50 text-pink-700' :
      color === 'blue' ? 'bg-blue-50 text-blue-700' :
      color === 'amber' ? 'bg-amber-50 text-amber-700' :
      'bg-slate-50 text-slate-700'
    }`}>
      <Icon size={20} />
    </div>
    <div className="flex-1 min-w-0">
      <h3 className="text-sm font-medium text-slate-900 group-hover:text-emerald-700 transition-colors">{title}</h3>
      <p className="text-xs text-slate-500 truncate">{description}</p>
    </div>
    <ChevronRight size={16} className="text-slate-400 group-hover:text-emerald-600 transition-colors" />
  </Link>
);

// Status Item Component
const StatusItem = ({ label, status }) => (
  <div className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
    <span className="text-sm text-slate-700">{label}</span>
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
      status === 'pass' ? 'bg-emerald-100 text-emerald-800' :
      status === 'warn' ? 'bg-amber-100 text-amber-800' :
      'bg-red-100 text-red-800'
    }`}>
      {status === 'pass' ? 'Complete' : status === 'warn' ? 'In Progress' : 'Missing'}
    </span>
  </div>
);

const Dashboard = () => {
  const [summary, setSummary] = useState(null);
  const [complianceStatus, setComplianceStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDashboardData = async () => {
    const [summaryData, complianceData] = await Promise.all([
      apiFetch(`${API}/dashboard/summary`),
      apiFetch(`${API}/compliance/quick-status`)
    ]);

    if (complianceData?._error) {
      console.log('Compliance fetch error:', complianceData.status);
    }

    if (summaryData?._error) {
      console.error('Error fetching dashboard data:', summaryData.message);
      setLoading(false);
      return;
    }

    setSummary(summaryData);
    setComplianceStatus(complianceData?._error ? null : complianceData);
    setLoading(false);
  };

  useEffect(() => {
    // Initial fetch
    fetchDashboardData();
    
    // Retry after a short delay if token might not be ready
    const retryTimer = setTimeout(() => {
      const token = localStorage.getItem('token');
      if (token && (!complianceStatus || complianceStatus.quick_score === undefined)) {
        fetchDashboardData();
      }
    }, 1000);
    
    // Re-fetch when localStorage changes (e.g., after login)
    const handleStorageChange = (e) => {
      if (e.key === 'token' && e.newValue) {
        fetchDashboardData();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearTimeout(retryTimer);
    };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-emerald-700 flex items-center justify-center animate-pulse">
            <Leaf size={24} className="text-white" />
          </div>
          <p className="text-sm text-slate-500">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const complianceScore = complianceStatus?.quick_score ?? (localStorage.getItem('token') ? 0 : 78);
  const isDemo = !localStorage.getItem('token');
  
  // ESG breakdown data
  const esgData = [
    { name: 'Environmental', value: summary?.indicators?.by_category?.['Environmental'] || 0, fill: '#064E3B' },
    { name: 'Social', value: summary?.indicators?.by_category?.['Social'] || 0, fill: '#BE185D' },
    { name: 'Governance', value: summary?.indicators?.by_category?.['Governance'] || 0, fill: '#1E3A8A' },
  ];

  // Monthly trend mock data
  const trendData = [
    { month: 'Jul', score: 25 },
    { month: 'Aug', score: 32 },
    { month: 'Sep', score: 38 },
    { month: 'Oct', score: 42 },
    { month: 'Nov', score: 48 },
    { month: 'Dec', score: Math.round(complianceScore) },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard">
      {/* Page Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Dashboard
          </h1>
          <p className="text-lg text-slate-600">
            Welcome back. Here's your ESG compliance overview.
          </p>
        </div>
        <Link
          to="/compliance-center"
          data-testid="run-gap-analysis-btn"
          className="flex items-center gap-2 px-4 py-2.5 bg-emerald-700 text-white rounded-lg hover:bg-emerald-800 transition-colors text-sm font-medium shadow-sm"
        >
          Run Gap Analysis
          <ArrowRight size={16} />
        </Link>
      </div>

      {/* Unified Dashboard Banner */}
      <Link
        to="/disclosure-os"
        data-testid="unified-dashboard-banner"
        className="block w-full bg-gradient-to-r from-emerald-900/90 to-cyan-900/90 border border-emerald-500/30 rounded-xl p-5 hover:border-emerald-400/50 transition-all group"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
              <Sparkles size={20} className="text-emerald-400" />
            </div>
            <div>
              <h3 className="text-white font-semibold text-base">Unified Dashboard</h3>
              <p className="text-emerald-300/70 text-sm">4 persona views | 13 frameworks | Live data from 14 sources</p>
            </div>
          </div>
          <ArrowRight size={20} className="text-emerald-400 group-hover:translate-x-1 transition-transform" />
        </div>
      </Link>

      {/* Main Grid */}
      <div className="grid grid-cols-12 gap-6">
        {/* Compliance Score - Large Card */}
        <div className="col-span-12 lg:col-span-4 bg-white border border-slate-200 rounded-lg p-6 flex flex-col items-center justify-center" data-testid="compliance-score-card">
          <span className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-4">Overall Compliance Score</span>
          <ComplianceScoreRing score={Math.round(complianceScore)} />
          <div className="mt-4">
            {isDemo ? (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                Demo Data - Sign in for real metrics
              </span>
            ) : complianceScore >= 70 ? (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                <CheckCircle size={14} className="mr-1" /> On Track
              </span>
            ) : complianceScore >= 40 ? (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                <AlertTriangle size={14} className="mr-1" /> Needs Attention
              </span>
            ) : (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                <AlertTriangle size={14} className="mr-1" /> Critical Gaps
              </span>
            )}
          </div>
        </div>

        {/* Compliance Trend Chart */}
        <div className="col-span-12 lg:col-span-8 bg-white border border-slate-200 rounded-lg p-6" data-testid="compliance-trend-card">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">Compliance Progress</span>
            <span className="text-xs text-slate-400">Last 6 months</span>
          </div>
          <div style={{ height: 192, minHeight: 192 }}>
            <ResponsiveContainer width="100%" height={192} minHeight={192}>
              <AreaChart data={trendData}>
                <defs>
                  <linearGradient id="colorScore" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#064E3B" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#064E3B" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis 
                  dataKey="month" 
                  stroke="#94A3B8"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis 
                  stroke="#94A3B8"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                  domain={[0, 100]}
                  tickFormatter={(v) => `${v}%`}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#fff',
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}
                  formatter={(value) => [`${value}%`, 'Score']}
                />
                <Area 
                  type="monotone" 
                  dataKey="score" 
                  stroke="#064E3B" 
                  strokeWidth={2}
                  fillOpacity={1} 
                  fill="url(#colorScore)" 
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* KPI Cards Row */}
        <div className="col-span-12 sm:col-span-6 lg:col-span-3">
          <KPICard 
            label="Data Sources" 
            value={`${summary?.datasources?.migrated || 0}/${summary?.datasources?.total || 0}`}
            delta={`${Math.round((summary?.datasources?.migrated / summary?.datasources?.total) * 100) || 0}% migrated`}
            deltaType="positive"
            icon={FileText}
            color="emerald"
          />
        </div>
        <div className="col-span-12 sm:col-span-6 lg:col-span-3">
          <KPICard 
            label="ESG Indicators" 
            value={summary?.indicators?.total || 0}
            delta="ESRS aligned"
            icon={BarChart3}
            color="emerald"
          />
        </div>
        <div className="col-span-12 sm:col-span-6 lg:col-span-3">
          <KPICard 
            label="Critical Risks" 
            value={summary?.risks?.critical || 0}
            delta={`${summary?.risks?.high || 0} high priority`}
            deltaType="negative"
            icon={AlertTriangle}
            color="amber"
          />
        </div>
        <div className="col-span-12 sm:col-span-6 lg:col-span-3">
          <KPICard 
            label="Frameworks" 
            value={summary?.frameworks?.length || 0}
            delta="Active"
            icon={Building2}
            color="blue"
          />
        </div>

        {/* Data Coverage Status */}
        <div className="col-span-12 md:col-span-6 bg-white border border-slate-200 rounded-lg p-6" data-testid="data-coverage-card">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">Data Coverage Status</span>
            <Link to="/compliance-center" className="text-xs text-emerald-700 hover:underline font-medium">View Details</Link>
          </div>
          <div className="space-y-0">
            <StatusItem 
              label="Environmental Data" 
              status={complianceStatus?.data_coverage?.emissions ? 'pass' : 'fail'} 
            />
            <StatusItem 
              label="Social/Workforce Data" 
              status={complianceStatus?.data_coverage?.workforce ? 'pass' : 'fail'} 
            />
            <StatusItem 
              label="Governance Metrics" 
              status={complianceStatus?.data_coverage?.governance ? 'pass' : (summary?.indicators?.by_category?.['G1'] > 0 ? 'pass' : 'fail')} 
            />
            <StatusItem 
              label="Audit Trail" 
              status="pass" 
            />
          </div>
        </div>

        {/* ESG Breakdown */}
        <div className="col-span-12 md:col-span-6 bg-white border border-slate-200 rounded-lg p-6" data-testid="esg-breakdown-card">
          <span className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-4 block">ESG Indicator Breakdown</span>
          <div className="flex items-center gap-8">
            <div style={{ width: 144, height: 144, minWidth: 144, minHeight: 144 }}>
              <ResponsiveContainer width={144} height={144} minWidth={144} minHeight={144}>
                <PieChart>
                  <Pie
                    data={esgData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={60}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {esgData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff',
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px',
                      fontSize: '12px'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex-1 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#064E3B' }} />
                  <span className="text-sm text-slate-700">Environmental</span>
                </div>
                <span className="text-sm font-semibold text-slate-900">{esgData[0].value}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#BE185D' }} />
                  <span className="text-sm text-slate-700">Social</span>
                </div>
                <span className="text-sm font-semibold text-slate-900">{esgData[1].value}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#1E3A8A' }} />
                  <span className="text-sm text-slate-700">Governance</span>
                </div>
                <span className="text-sm font-semibold text-slate-900">{esgData[2].value}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="col-span-12 bg-white border border-slate-200 rounded-lg p-6" data-testid="quick-actions-card">
          <span className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-4 block">Quick Actions</span>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <QuickActionCard 
              title="Upload Data"
              description="Import CSV, Excel, or JSON files"
              icon={FileText}
              to="/data-upload"
              color="emerald"
            />
            <QuickActionCard 
              title="View Emissions"
              description="Track Scope 1, 2 & 3 GHG"
              icon={Leaf}
              to="/emissions-tracking"
              color="emerald"
            />
            <QuickActionCard 
              title="Methodology Transparency"
              description="Review calculations & derivations"
              icon={Shield}
              to="/audit/methodology"
              color="blue"
            />
            <QuickActionCard
              title="Framework Mapping"
              description="Explore KPI-to-Law mappings"
              icon={Target}
              to="/cross-framework-mapping"
              color="amber"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
